(() => {
  "use strict";

  const clean = (value = "") =>
    String(value)
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+/g, " ")
      .replace(/\s*\n\s*/g, "\n")
      .trim();

  const stripLead = (value = "") =>
    clean(value)
      .replace(/^[✓✔•·▪◦–—-]+\s*/u, "")
      .replace(/^\d+[.)]\s*/, "")
      .trim();

  const key = (value = "") =>
    stripLead(value)
      .toLowerCase()
      .replace(/[“”'".,:;!?()[\]{}]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const unique = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const k = key(item);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  function amsterdamTodayUTC() {
    const now = new Date();
    let year;
    let month;
    let day;

    try {
      const amsterdam = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Amsterdam",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = Object.fromEntries(
        amsterdam
          .formatToParts(now)
          .filter((part) => part.type !== "literal")
          .map((part) => [part.type, part.value])
      );
      year = Number(parts.year);
      month = Number(parts.month) - 1;
      day = Number(parts.day);
    } catch (error) {
      year = now.getFullYear();
      month = now.getMonth();
      day = now.getDate();
    }

    return new Date(Date.UTC(year, month, day, 12, 0, 0));
  }

  function expectedDeliveryRange(minDays = 3, maxDays = 6) {
    const today = amsterdamTodayUTC();
    const earliest = new Date(today);
    const latest = new Date(today);
    earliest.setUTCDate(earliest.getUTCDate() + minDays);
    latest.setUTCDate(latest.getUTCDate() + maxDays);

    const dayFormat = new Intl.DateTimeFormat("nl-NL", {
      day: "numeric",
      timeZone: "UTC",
    });
    const fullFormat = new Intl.DateTimeFormat("nl-NL", {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    });

    if (
      earliest.getUTCFullYear() === latest.getUTCFullYear() &&
      earliest.getUTCMonth() === latest.getUTCMonth()
    ) {
      return `${dayFormat.format(earliest)} - ${fullFormat.format(latest)}`;
    }

    return `${fullFormat.format(earliest)} - ${fullFormat.format(latest)}`;
  }

  function initExpectedDelivery(scope = document) {
    scope.querySelectorAll("[data-expected-delivery]").forEach((node) => {
      node.textContent = expectedDeliveryRange(3, 6);
    });
  }

  function headingKey(value = "") {
    return clean(value)
      .toUpperCase()
      .replace(/[.:]+$/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function sectionHeading(text) {
    const h = headingKey(text);

    if (/^(IN HET KORT|HIGHLIGHTS?|FEATURES?|KENMERKEN)$/.test(h)) {
      return { section: "short", label: "IN HET KORT" };
    }
    if (/^(DETAILS|OVER DIT PRODUCT)$/.test(h)) {
      return { section: "details", label: "Details" };
    }
    if (/^(MATERIAAL( & (WASVOORSCHRIFT|ONDERHOUD))?|MATERIAL( & CARE)?|CARE)$/.test(h)) {
      return {
        section: "material",
        label: /ONDERHOUD|CARE/.test(h) ? "Materiaal & onderhoud" : "Materiaal & wasvoorschrift",
      };
    }
    if (/^(PASVORM|FIT|MATERIAAL & PASVORM)$/.test(h)) {
      return { section: "fit", label: "Pasvorm" };
    }
    if (/^(AFMETINGEN & PASVORM|AFMETINGEN & GEBRUIK|SPECIFICATIES)$/.test(h)) {
      return {
        section: "fit",
        label: h === "SPECIFICATIES" ? "Specificaties" : h.toLowerCase().replace(/^./, (c) => c.toUpperCase()),
      };
    }
    if (/^(PERFECT VOOR|STYLING|STYLING TIP|SPORT-BH|HIGH-WAIST BIKER SHORT)$/.test(h)) {
      const label = h.toLowerCase().replace(/(^|\s|-)\S/g, (c) => c.toUpperCase());
      return { section: "detailsExtra", label };
    }
    if (/^(VERZENDING( & RETOUR)?|SHIPPING( & RETURNS?)?|RETOUR)$/.test(h)) {
      return { section: "shipping", label: "Verzending & retour" };
    }
    return null;
  }

  function isHeadingNode(node) {
    const tag = node.tagName;
    if (/^H[1-6]$/.test(tag)) return true;
    if (tag !== "P" && tag !== "DIV") return false;

    const text = clean(node.textContent || "");
    if (!text || text.length > 90) return false;
    if (sectionHeading(text)) return true;

    const children = Array.from(node.children);
    return children.length > 0 && children.every((child) => ["STRONG", "B", "EM", "BR"].includes(child.tagName));
  }

  function collectBlocks(root) {
    const blocks = [];

    const walk = (parent) => {
      Array.from(parent.children).forEach((child) => {
        const tag = child.tagName;
        if (["DIV", "SECTION", "ARTICLE"].includes(tag) && !isHeadingNode(child)) {
          walk(child);
        } else {
          blocks.push(child);
        }
      });
    };

    walk(root);
    return blocks;
  }

  function isBrandSignoff(text = "") {
    const value = clean(text);
    return (
      /^GYMBABE\s*[—–-]/i.test(value) ||
      /^GYMBABE\s+(ISN['’]?T|MADE|BUILT)/i.test(value) ||
      /^TAKE YOUR GYMBABE MINDSET/i.test(value)
    );
  }

  function shortItemsFromBlock(block) {
    if (["UL", "OL"].includes(block.tagName)) {
      return Array.from(block.querySelectorAll(":scope > li"))
        .map((li) => stripLead(li.textContent || ""))
        .filter(Boolean);
    }

    const raw = clean(block.textContent || "");
    if (!raw) return [];

    const lines = raw
      .split(/\n+/)
      .map(stripLead)
      .filter(Boolean);

    if (lines.length > 1) return lines;
    return [stripLead(raw)];
  }

  function cloneClean(block) {
    const clone = block.cloneNode(true);
    clone.removeAttribute("id");
    clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
    return clone;
  }

  function miniHeading(label) {
    const h = document.createElement("h3");
    h.className = "gb-inline-subheading";
    h.textContent = label;
    return h;
  }

  function parseStructured(source) {
    const result = {
      found: false,
      short: [],
      details: [],
      material: [],
      fit: [],
      materialLabel: "Materiaal & wasvoorschrift",
      fitLabel: "Pasvorm",
    };

    let current = "preamble";
    const blocks = collectBlocks(source);

    blocks.forEach((block) => {
      const text = clean(block.textContent || "");
      if (!text) return;

      const heading = isHeadingNode(block) ? sectionHeading(text) : null;
      if (heading) {
        result.found = true;
        if (heading.section === "material") result.materialLabel = heading.label;
        if (heading.section === "fit") result.fitLabel = heading.label;

        if (heading.section === "detailsExtra") {
          current = "details";
          result.details.push(miniHeading(heading.label));
        } else {
          current = heading.section;
        }
        return;
      }

      if (!result.found || current === "preamble" || current === "shipping") return;
      if (isBrandSignoff(text)) return;

      if (current === "short") {
        result.short.push(...shortItemsFromBlock(block));
        return;
      }

      if (["details", "material", "fit"].includes(current)) {
        result[current].push(cloneClean(block));
      }
    });

    result.short = unique(result.short).slice(0, 10);
    return result;
  }

  const materialWords = /(\b\d{1,3}\s*%|nylon|polyamide|polyester|elastaan|elastane|spandex|katoen|cotton|viscose|modal|acryl|acrylic|wol\b|wool|linnen|linen|seamless\s*knit|gebreid|knit|gsm\b|ademend|breathable|sneldrogend|quick[- ]?dry|was(?:sen|baar)?\b|wash|droger|dryer|30\s*°?\s*c|40\s*°?\s*c)/i;
  const fitWords = /(pasvorm|\bfit\b|high[- ]?waist|mid[- ]?waist|low[- ]?waist|taille|waistband|tummy[- ]?control|second[- ]?skin|compress(?:ie|ion)?|sculpt|support|stretch|valt\b|maat\b|size\b|model\b|oversized|relaxed|straight|slim|cropped|bodycon|snatched|losvallend|aansluitend)/i;
  const noiseWords = /(gratis verzending|14 dagen retour|veilig betalen|klarna|ideal|verzendbeleid|retourbeleid)/i;

  function legacyParse(source) {
    const bullets = [];
    const material = [];
    const fit = [];
    const paragraphs = [];

    const nodes = Array.from(source.querySelectorAll("p,li"));
    nodes.forEach((node) => {
      const line = stripLead(node.textContent || "");
      if (!line || line.length < 2 || noiseWords.test(line) || isBrandSignoff(line)) return;

      const explicitBullet = node.tagName === "LI" || /^[✓✔•·▪◦–—-]\s*/u.test(clean(node.textContent || ""));
      const materialLike = materialWords.test(line);
      const fitLike = fitWords.test(line);

      if (materialLike) material.push(line);
      if (fitLike && !materialLike && line.length <= 240) fit.push(line);
      if (explicitBullet && !materialLike) bullets.push(line);
      if (node.tagName === "P" && line.length >= 35 && !materialLike && !fitLike) paragraphs.push(line);
    });

    return {
      bullets: unique(bullets).slice(0, 8),
      material: unique(material).slice(0, 5),
      fit: unique(fit).slice(0, 5),
      paragraphs: unique(paragraphs).slice(0, 4),
    };
  }

  function makeCheckItem(text) {
    const li = document.createElement("li");
    li.innerHTML = '<svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 12.5l5 5L20 7"></path></svg><span></span>';
    li.querySelector("span").textContent = text;
    return li;
  }

  function hasContent(container) {
    return !!container && clean(container.textContent || "").length > 0;
  }

  function replaceWithNodes(container, nodes) {
    if (!container || !nodes.length) return false;
    container.innerHTML = "";
    nodes.forEach((node) => container.appendChild(node.cloneNode(true)));
    return true;
  }

  function replaceWithParagraphs(container, lines) {
    if (!container || !lines.length) return false;
    container.innerHTML = "";
    lines.forEach((line) => {
      const p = document.createElement("p");
      p.textContent = line;
      container.appendChild(p);
    });
    return true;
  }

  function fillShort(list, structured, legacy) {
    if (!list) return;

    if (structured.short.length) {
      list.innerHTML = "";
      structured.short.forEach((item) => list.appendChild(makeCheckItem(item)));
    } else if (!hasContent(list) && legacy.bullets.length) {
      legacy.bullets.forEach((item) => list.appendChild(makeCheckItem(item)));
    }

    const card = list.closest("[data-short-card]");
    if (card) card.hidden = !hasContent(list);
  }

  function initOne(root) {
    if (!root || root.dataset.smartReady === "true") return;
    const source = root.querySelector("[data-description-source]");
    if (!source) return;

    const structured = parseStructured(source);
    const legacy = legacyParse(source);

    fillShort(root.querySelector("[data-short-list]"), structured, legacy);

    const detailsSection = root.querySelector("[data-details-section]");
    const detailsContent = root.querySelector("[data-details-content]");
    if (structured.details.length) {
      replaceWithNodes(detailsContent, structured.details);
    } else if (!hasContent(detailsContent) && legacy.paragraphs.length) {
      replaceWithParagraphs(detailsContent, legacy.paragraphs);
    }
    if (detailsSection) detailsSection.hidden = !hasContent(detailsContent);

    const materialSection = root.querySelector("[data-material-section]");
    const materialContent = root.querySelector("[data-material-content]");
    const materialTitle = root.querySelector("[data-material-title]");
    if (structured.material.length) {
      replaceWithNodes(materialContent, structured.material);
      if (materialTitle) materialTitle.textContent = structured.materialLabel;
    } else if (!hasContent(materialContent) && legacy.material.length) {
      replaceWithParagraphs(materialContent, legacy.material);
    }
    if (materialSection) materialSection.hidden = !hasContent(materialContent);

    const fitSection = root.querySelector("[data-fit-section]");
    const fitContent = root.querySelector("[data-fit-content]");
    const fitTitle = root.querySelector("[data-fit-title]");
    if (structured.fit.length) {
      replaceWithNodes(fitContent, structured.fit);
      if (fitTitle) fitTitle.textContent = structured.fitLabel;
    } else if (!hasContent(fitContent) && legacy.fit.length) {
      replaceWithParagraphs(fitContent, legacy.fit);
    }
    if (fitSection) fitSection.hidden = !hasContent(fitContent);

    root.dataset.smartReady = "true";
  }

  function init(scope = document) {
    initExpectedDelivery(scope);
    scope.querySelectorAll("[data-smart-product-content]").forEach(initOne);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
